// Mock data for the Loft Drill redesign prototype. Stands in for what drill mode
// reads live from the React tree + registry. This is a design prototype of the
// handover surface, not wired to the real inspect engine yet.

// A component earns its styling in exactly one of three legit ways (pipe /
// wrapped / theme); visual sx on a live instance is flagged. Same thesis as the real inspect
// layer — see front-end/designs/PURPOSE.md.
export type Grade = 'pipe' | 'wrapped' | 'theme' | 'violation';

export type GradeMeta = {
  label: string;
  accent: string; // dot + text colour
  tint: string; // selected-row background
  ok: boolean; // legit grade (square dot) vs violation (round dot)
};

export const GRADES: Record<Grade, GradeMeta> = {
  pipe: { label: 'Pipe', accent: '#3F7D5F', tint: '#EAF4EE', ok: true },
  wrapped: { label: 'Wrapped', accent: '#6E8A3B', tint: '#F0F4E1', ok: true },
  theme: { label: 'Theme', accent: '#1F7A74', tint: '#E3F0EF', ok: true },
  violation: {
    label: 'Violation',
    accent: '#9C1111',
    tint: '#FBE8E8',
    ok: false,
  },
};

export const GRADE_ORDER: Grade[] = ['pipe', 'wrapped', 'theme', 'violation'];

export type TreeNode = {
  id: string;
  name: string;
  grade: Grade;
  note: string;
  children?: TreeNode[];
};

// The structure tree drill mode shows for the selected prototype.
export const TREE: TreeNode[] = [
  {
    id: 'screen',
    name: 'CarePlanScreen',
    grade: 'pipe',
    note: 'route · /care-plan',
    children: [
      { id: 'appbar', name: 'AppBar', grade: 'pipe', note: 'default header' },
      {
        id: 'scroll',
        name: 'ScrollView',
        grade: 'pipe',
        note: 'layout',
        children: [
          {
            id: 'promo',
            name: 'PromoBanner',
            grade: 'violation',
            note: 'inline #FF3D81',
          },
          {
            id: 'card',
            name: 'CareCard',
            grade: 'wrapped',
            note: '@loft/shared',
            children: [
              {
                id: 'avatar',
                name: 'Avatar',
                grade: 'theme',
                note: 'palette token',
              },
              {
                id: 'heading',
                name: 'Heading',
                grade: 'pipe',
                note: 'default text',
              },
              {
                id: 'progress',
                name: 'ProgressBar',
                grade: 'theme',
                note: 'custom.green',
              },
            ],
          },
          {
            id: 'section',
            name: 'SectionHeader',
            grade: 'pipe',
            note: '“Upcoming”',
          },
          {
            id: 'btn',
            name: 'PrimaryButton',
            grade: 'theme',
            note: 'palette.primary',
          },
        ],
      },
    ],
  },
];

// Ids of nodes with children — used to collapse everything at once.
export const PARENT_IDS = ['screen', 'scroll', 'card'];

// The redesign's proposed Loft chrome palette, taken from the Claude Design
// mockup. Prototype-local design tokens (candidates for the designs theme
// later), kept in one place so the page has no raw hex scattered through it.
export const CHROME = {
  bg: '#FAF9F6',
  surface: '#FFFFFF',
  canvas: '#EFEDE7',
  canvasDot: 'rgba(0,0,0,.055)',
  line: '#ECE7DD',
  lineSoft: '#F0ECE3',
  lineCard: '#E7E1D2',
  coral: '#EA6430',
  coralInk: '#c0451c',
  coralLine: '#EBCFC2',
  coralWash: '#FCF2ED',
  green: '#00433D',
  greenDark: '#005E5A',
  ink: '#1a1a1a',
  bezel: '#1c1c1e',
  toastInk: '#1c1c1e',
  sync: { bg: '#F1F7F2', border: '#DCE7DE', dot: '#51AC7C', text: '#2f6b4a' },
  violation: { bg: '#FBE8E8', border: '#F0C4C4', text: '#9C1111' },
  bannerPink: '#FF3D81',
} as const;
